import type { FC, ReactNode } from 'react'

import { Info } from 'lucide-react'
import { pad } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Enter, HeroTitle } from '@/components/motion/hero-title'
import { Reveal } from '@/components/motion/reveal'
import { siteConfig } from '@/constants/site'

export type LegalSection = {
    id: string
    title: string
    body: ReactNode
}

interface LegalPageProps {
    title: string
    description: string
    note: string
    sections: LegalSection[]
}

export const LegalPage: FC<LegalPageProps> = ({ title, description, note, sections }): ReactNode => {
    return (
        <Layout>
            <Seo title={title} description={description} />

            <section className='relative overflow-hidden pb-12 pt-36 md:pb-16 md:pt-44'>
                <div aria-hidden='true' className='pointer-events-none absolute -right-40 -top-40 size-[42rem] rounded-full bg-ember/[0.07] blur-3xl' />

                <Container className='relative'>
                    <Enter delay={60}>
                        <Eyebrow>The fine print</Eyebrow>
                    </Enter>

                    <HeroTitle text={title} className='mt-6 text-h1 font-semibold text-ink' />

                    <Enter delay={380} className='mt-8 max-w-2xl'>
                        <p className='flex items-start gap-3 rounded-[20px] border border-ink/10 bg-card p-5 text-[14.5px] leading-relaxed text-slate'>
                            <Info className='mt-0.5 size-4 shrink-0 text-ember' aria-hidden='true' />
                            {note}
                        </p>
                    </Enter>
                </Container>
            </section>

            <section className='pb-24 md:pb-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <nav aria-label="On this page" className='hidden lg:col-span-3 lg:block'>
                        <ol className='sticky top-32 space-y-1 border-l border-ink/10'>
                            {sections.map((section, index) => (
                                <li key={section.id}>
                                    <a
                                        href={`#${section.id}`}
                                        className='-ml-px flex gap-3 border-l border-transparent py-1.5 pl-4 text-[14px] text-slate transition-colors hover:border-ember hover:text-ink'
                                    >
                                        <span className='font-mono text-[11px] leading-[1.9]'>{pad(index + 1)}</span>
                                        {section.title}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>

                    <Reveal selector=':scope > section' className='border-t border-ink/10 lg:col-span-9'>
                        {sections.map((section, index) => (
                            <section
                                key={section.id}
                                id={section.id}
                                className='grid scroll-mt-28 gap-3 border-b border-ink/10 py-9 md:grid-cols-12 md:gap-6 md:py-11'
                            >
                                <h2 className='flex items-baseline gap-4 font-heading text-[1.5rem] font-semibold leading-tight tracking-[-0.03em] text-ink md:col-span-4 md:text-[1.7rem]'>
                                    <span className='font-mono text-[12px] font-normal tracking-normal text-slate'>{pad(index + 1)}</span>
                                    {section.title}
                                </h2>

                                <div className='max-w-2xl text-[16px] leading-relaxed text-slate md:col-span-8'>
                                    {section.body}
                                </div>
                            </section>
                        ))}
                    </Reveal>
                </Container>
            </section>

            <section className='pb-24 md:pb-32'>
                <Container>
                    <p className='font-mono text-[11.5px] uppercase tracking-[0.14em] text-slate'>
                        {siteConfig.legalName} · {siteConfig.address} · {siteConfig.phone}
                    </p>
                </Container>
            </section>
        </Layout>
    )
}
